import { SubtractiveGenerator } from 'game-logic';
import GameMetrics from '../helpers/game-metrics';
import { Cell } from './cell';
import { GridElement } from './grid-element';

export class Grid {
  public readonly cells: Cell[] = [];

  private readonly subGrids: GridElement[] = new Array(GameMetrics.GridSize)
    .fill(null)
    .map(() => new GridElement());
  private readonly rows: GridElement[] = new Array(GameMetrics.GridSize)
    .fill(null)
    .map(() => new GridElement());
  private readonly columns: GridElement[] = new Array(GameMetrics.GridSize)
    .fill(null)
    .map(() => new GridElement());

  public cellById(cellId: string): Cell | undefined {
    return this.cells.find((cell) => cell.id === cellId);
  }

  constructor() {
    for (let i = 0; i < GameMetrics.GridSize; ++i) {
      for (let j = 0; j < GameMetrics.GridSize; ++j) {
        const cell = new Cell(`${i}${j}`);
        this.cells.push(cell);

        const idx = Grid.getSubgridByCellCoords(i, j) - 1;
        cell.subgridIndex = idx;
        this.subGrids[cell.subgridIndex].cells.push(cell);

        this.rows[i].cells.push(cell);
        this.columns[j].cells.push(cell);
      }
    }
  }

  public gridElementsToCollect(): GridElement[] {
    return [
      ...this.rows.filter((element) => element.isFull),
      ...this.columns.filter((element) => element.isFull),
      ...this.subGrids.filter((element) => element.isFull),
    ];
  }

  public getCellsWithMultipliers(): Cell[] {
    return this.cells.filter((cell) => (cell.blockMultiplier ?? 0) > 0);
  }

  public getCellToAssignPoints(random: SubtractiveGenerator): Cell | null {
    const cellsWithPoints = this.cells.filter((cell) => cell.currentPointsAmount > 0);
    return cellsWithPoints.length < GameMetrics.MaxCellsWithPoints
      ? this.randomEmptyCell(random)
      : this.randomEmptyCellWithPoints(random);
  }

  private randomEmptyCell(random: SubtractiveGenerator): Cell | null {
    const emptyCells = this.cells.filter(
      (cell) => cell.currentBlock === null && cell.currentPointsAmount === 0,
    );
    if (emptyCells.length === 0) {
      return null;
    }
    const randomPos = random.next() % emptyCells.length;
    return emptyCells[randomPos];
  }

  private randomEmptyCellWithPoints(random: SubtractiveGenerator): Cell | null {
    const emptyCells = this.cells.filter(
      (cell) => cell.currentBlock === null && cell.currentPointsAmount > 0,
    );
    if (emptyCells.length === 0) {
      return null;
    }
    const randomPos = random.next() % emptyCells.length;
    return emptyCells[randomPos];
  }

  private static getSubgridByCellCoords(row: number, col: number): number {
    const majorRow = Math.floor(row / GameMetrics.SubGridSize);
    const majorCol = Math.floor(col / GameMetrics.SubGridSize);
    const result = majorCol + majorRow * GameMetrics.SubGridSize + 1;
    return result;
  }
}
